import React, { useState, useEffect } from 'react';
import { openPrintWindow } from '../../utils/printDocument';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Print as PrintIcon,
  Info as InfoIcon,
  EventBusy as EventBusyIcon,
  CalendarToday as CalendarIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import PersonnelDetails from './PersonnelDetails';
import Settings from './Settings';
import AbsenceManagement from './AbsenceManagement';

// Fonction pour calculer le nombre d'heures entre deux horaires
const calculateHoursBetween = (start, end) => {
  if (!start || !end) {
    return 0;
  }
  const [startHours, startMinutes] = start.split(':').map(Number);
  const [endHours, endMinutes] = end.split(':').map(Number);
  const hours = (endHours + endMinutes/60) - (startHours + startMinutes/60);
  return hours;
};

// Fonction pour calculer le total d'heures hebdomadaires
const calculateWeeklyHours = (schedule) => {
  if (!schedule) {
    return 0;
  }
  
  let totalHours = 0;
  const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
  
  days.forEach(day => {
    const daySchedule = schedule[day];
    
    if (daySchedule?.active) {
      if (daySchedule.withBreak) {
        const morningHours = calculateHoursBetween(daySchedule.morning?.start, daySchedule.morning?.end);
        const afternoonHours = calculateHoursBetween(daySchedule.afternoon?.start, daySchedule.afternoon?.end);
        totalHours += morningHours + afternoonHours;
      } else {
        const continuousHours = calculateHoursBetween(daySchedule.continuous?.start, daySchedule.continuous?.end);
        totalHours += continuousHours;
      }
    }
  });
  
  const roundedHours = Math.round(totalHours * 100) / 100;
  return roundedHours;
};

// Composant pour les cartes de statistiques
const StatCard = ({ title, value, icon, color, onClick }) => (
  <Card 
    sx={{ 
      height: '100%', 
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: 3,
      } : {},
    }} 
    onClick={onClick}
  >
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4">
            {value}
          </Typography>
        </Box>
        <Box 
          sx={{ 
            backgroundColor: `${color}20`,
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Composant TabPanel local
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [stats, setStats] = useState({
    totalPersonnel: 0,
    totalDepartments: 0,
    totalMessages: 0,
    totalNotifications: 0
  });
  const [personnelByDepartment, setPersonnelByDepartment] = useState({});
  const [recentPersonnel, setRecentPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPersonnelByDepartment, setFilteredPersonnelByDepartment] = useState({});
  useEffect(() => {
    const fetchStats = async () => {
      if (!userData?.establishmentId) {
        return;
      }
      try {
        setLoading(true);
        
        // Récupérer les informations de l'établissement
        const estabDoc = await getDocs(query(
          collection(db, 'establishments'),
          where('__name__', '==', userData.establishmentId)
        ));
        if (!estabDoc.empty) {
          setEstablishmentInfo(estabDoc.docs[0].data());
        }
        
        // Récupérer le personnel par département
        const usersRef = collection(db, 'users');
        const usersQuery = query(
          usersRef,
          where('establishmentId', '==', userData.establishmentId),
          where('role', 'in', ['personnel', 'chefService', 'directionRH'])
        );
        
        const usersSnapshot = await getDocs(usersQuery);
        
        const users = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Calculer les statistiques par service
        const serviceStats = {};
        users.forEach(user => {
          if (!serviceStats[user.service]) {
            serviceStats[user.service] = {
              total: 0,
              active: 0,
              personnel: [],
            };
          }
          serviceStats[user.service].total++;
          if (user.status === 'actif') {
            serviceStats[user.service].active++;
          }
          serviceStats[user.service].personnel.push(user);
        });

        setPersonnelByDepartment(serviceStats);
        setFilteredPersonnelByDepartment(serviceStats);

        // Récupérer les 5 derniers membres du personnel ajoutés
        const recentPersonnelQuery = query(
          usersRef,
          where('establishmentId', '==', userData.establishmentId),
          where('role', 'in', ['personnel', 'chefService', 'directionRH']),
          orderBy('createdAt', 'desc')
        );
        const recentPersonnelSnapshot = await getDocs(recentPersonnelQuery);
        
        const recentPersonnelList = recentPersonnelSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .slice(0, 5);

        setRecentPersonnel(recentPersonnelList);

        // Récupérer les notifications non lues pour l'admin
        const notificationsQuery = query(
          collection(db, 'notifications'),
          where('destinataireId', '==', currentUser.uid),
          where('read', '==', false)
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);
        const unreadNotifications = notificationsSnapshot.size;

        // Mettre à jour les statistiques globales
        setStats({
          totalPersonnel: users.length,
          totalDepartments: Object.keys(serviceStats).length,
          totalMessages: 0,
          totalNotifications: unreadNotifications
        });

        setLoading(false);
      } catch (err) {
        console.error('Erreur détaillée lors de la récupération des statistiques:', {
          message: err.message,
          code: err.code,
          details: err.details,
          userData: userData
        });
        setError('Erreur lors de la récupération des données');
        setLoading(false);
      }
    };

    fetchStats();
  }, [userData]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Filtrer le personnel en fonction de la recherche
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPersonnelByDepartment(personnelByDepartment);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = {};

    Object.entries(personnelByDepartment).forEach(([service, deptData]) => {
      const filteredPersonnel = deptData.personnel.filter(person => {
        const fullName = `${person.firstName} ${person.lastName}`.toLowerCase();
        const email = (person.email || '').toLowerCase();
        const grade = (person.grade || '').toLowerCase();
        
        return fullName.includes(searchLower) || 
               email.includes(searchLower) || 
               grade.includes(searchLower);
      });

      if (filteredPersonnel.length > 0) {
        filtered[service] = {
          ...deptData,
          personnel: filteredPersonnel,
          total: filteredPersonnel.length
        };
      }
    });

    setFilteredPersonnelByDepartment(filtered);
  }, [searchTerm, personnelByDepartment]);

  const handlePrintPersonnel = (serviceFilter = null) => {
    const extraCss = `
          .print-page {
            font-size: 11px;
            line-height: 1.4;
          }
          .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 20px; 
            padding-bottom: 15px;
            border-bottom: 3px solid #1976d2;
          }
          .header-left {
            flex: 1;
            text-align: left;
          }
          .header-center {
            flex: 2;
            text-align: center;
          }
          .header-right {
            flex: 1;
            text-align: right;
          }
          .logo {
            max-width: 80px;
            max-height: 80px;
            object-fit: contain;
          }
          .establishment-name {
            font-size: 16px;
            font-weight: bold;
            color: #1976d2;
            margin-bottom: 5px;
          }
          .header h1 {
            margin: 0 0 5px 0;
            font-size: 22px;
            color: #1976d2;
          }
          .header .info {
            font-size: 12px;
            color: #666;
            margin: 3px 0;
          }
          .stats {
            background-color: #f5f5f5;
            padding: 10px;
            margin-bottom: 20px;
            border-radius: 5px;
            display: flex;
            justify-content: space-around;
          }
          .stat-item {
            text-align: center;
          }
          .stat-label {
            font-size: 10px;
            color: #666;
          }
          .stat-value {
            font-size: 16px;
            font-weight: bold;
            color: #1976d2;
          }
          .service-section {
            margin-bottom: 25px;
            page-break-inside: avoid;
          }
          .service-title {
            background-color: #1976d2;
            color: white;
            padding: 8px 12px;
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 10px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #e3f2fd;
            font-weight: bold;
            font-size: 10px;
            color: #1976d2;
          }
          td {
            font-size: 10px;
          }
          .grade {
            background-color: #fff3e0;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 9px;
            display: inline-block;
          }
          .role {
            background-color: #e8f5e9;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 9px;
            display: inline-block;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 9px;
            color: #666;
            border-top: 1px solid #ddd;
            padding-top: 10px;
          }
          @media print {
            .service-section { page-break-inside: avoid; }
          }
    `;

    const bodyHtml = `
        <div class="print-page">
        <div class="header">
          <div class="header-left">
            ${establishmentInfo?.logo ? `<img src="${establishmentInfo.logo}" alt="Logo" class="logo" />` : ''}
          </div>
          <div class="header-center">
            <div class="establishment-name">${establishmentInfo?.name || 'Établissement scolaire'}</div>
            <h1>LISTE DU PERSONNEL${serviceFilter ? ` - ${serviceFilter}` : ''}</h1>
            <div class="info">Année scolaire: ${new Date().getFullYear()}-${new Date().getFullYear() + 1}</div>
            <div class="info">Date d'impression: ${new Date().toLocaleDateString('fr-FR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</div>
          </div>
          <div class="header-right">
            ${establishmentInfo?.logo ? `<img src="${establishmentInfo.logo}" alt="Logo" class="logo" />` : ''}
          </div>
        </div>

        <div class="stats">
          <div class="stat-item">
            <div class="stat-label">Total Personnel</div>
            <div class="stat-value">${stats.totalPersonnel}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Services</div>
            <div class="stat-value">${stats.totalDepartments}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">Personnel Actif</div>
            <div class="stat-value">${Object.values(personnelByDepartment).reduce((sum, dept) => sum + dept.active, 0)}</div>
          </div>
        </div>

        ${Object.entries(personnelByDepartment)
          .filter(([service]) => !serviceFilter || service === serviceFilter)
          .map(([service, deptData]) => `
          <div class="service-section">
            <div class="service-title">
              ${service} (${deptData.total} personne${deptData.total > 1 ? 's' : ''})
            </div>
            <table>
              <thead>
                <tr>
                  <th style="width: 5%;">N°</th>
                  <th style="width: 25%;">Nom & Prénom</th>
                  <th style="width: 20%;">Email</th>
                  <th style="width: 15%;">Téléphone</th>
                  <th style="width: 15%;">Grade</th>
                  <th style="width: 10%;">Rôle</th>
                  <th style="width: 10%;">Statut</th>
                </tr>
              </thead>
              <tbody>
                ${deptData.personnel.map((person, index) => `
                  <tr>
                    <td style="text-align: center;">${index + 1}</td>
                    <td><strong>${person.firstName || ''} ${person.lastName || ''}</strong></td>
                    <td>${person.email || 'N/A'}</td>
                    <td>${person.phone || 'N/A'}</td>
                    <td><span class="grade">${person.grade || 'Non spécifié'}</span></td>
                    <td><span class="role">${person.role || 'N/A'}</span></td>
                    <td style="text-align: center;">${person.status === 'actif' ? '✓ Actif' : '✗ Inactif'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `).join('')}

        <div class="footer">
          <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
          <p>Établissement: ${establishmentInfo?.name || 'N/A'} - Généré par: ${userData.firstName || ''} ${userData.lastName || ''}</p>
        </div>
        </div>`;

    openPrintWindow(
      `Liste du Personnel - ${userData.establishmentName || 'Établissement'}`,
      bodyHtml,
      extraCss,
      'height=900,width=1000',
      { singlePage: false, pageMargin: '15mm' }
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Personnel"
            value={stats.totalPersonnel}
            icon={<PeopleIcon sx={{ color: '#1976d2' }} />}
            color="#1976d2"
            onClick={() => setSelectedTab(1)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Départements"
            value={stats.totalDepartments}
            icon={<BusinessIcon sx={{ color: '#2e7d32' }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Messages"
            value={stats.totalMessages}
            icon={<MessageIcon sx={{ color: '#ed6c02' }} />}
            color="#ed6c02"
            onClick={() => navigate('/admin/messages')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Notifications"
            value={stats.totalNotifications}
            icon={<NotificationsIcon sx={{ color: '#9c27b0' }} />}
            color="#9c27b0"
            onClick={() => navigate('/admin/notifications')}
          />
        </Grid>
      </Grid>

      {/* Ligne des boutons d'action */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<CalendarIcon />}
            onClick={() => navigate('/admin/calendar')}
            sx={{ height: '100%' }}
          >
            Calendrier Scolaire
          </Button>
        </Grid>
      </Grid>

      {/* Onglets */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="Tableau de bord" />
          <Tab label="Personnel" />
          <Tab label="Absences" />
          <Tab label="Messages" />
          <Tab label="Paramètres" />
        </Tabs>

        {/* Contenu des onglets */}
        <TabPanel value={selectedTab} index={0}>
          <Box p={3}>
            <Typography variant="h6" gutterBottom>
              Personnel récent
            </Typography>
            <List>
              {recentPersonnel.map((person) => (
                <React.Fragment key={person.id}>
                  <ListItem
                    button
                    onClick={() => {
                      setSelectedPersonnel(person);
                      setDetailsOpen(true);
                    }}
                  >
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${person.firstName} ${person.lastName}`}
                      secondary={`${person.service || 'Aucun service'} - ${person.role}`}
                    />
                    <ArrowForwardIcon />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Box>
        </TabPanel>

        <TabPanel value={selectedTab} index={1}>
          <Box p={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
              <Typography variant="h6">
                Personnel par service
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1, maxWidth: 600 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Rechercher par nom, email ou grade..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={() => handlePrintPersonnel(null)}
                  color="primary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Imprimer
                </Button>
              </Box>
            </Box>
            
            {Object.keys(filteredPersonnelByDepartment).length === 0 && searchTerm && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Aucun personnel trouvé pour "{searchTerm}"
              </Alert>
            )}
            
            {Object.entries(filteredPersonnelByDepartment).map(([dept, deptData]) => (
              <Box key={dept} sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="primary">
                    {dept}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={() => handlePrintPersonnel(dept)}
                    color="primary"
                  >
                    Imprimer ce service
                  </Button>
                </Box>
                <Grid container spacing={3}>
                  {deptData.personnel.map((person) => (
                    <Grid item xs={12} sm={6} md={4} key={person.id}>
                      <Card 
                        elevation={3}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s ease-in-out'
                          }
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, pb: 2 }}>
                          <Typography variant="h6" component="h2" gutterBottom sx={{ 
                            fontWeight: 'bold',
                            color: '#1976d2',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <PersonIcon sx={{ fontSize: 20 }} />
                            {`${person.firstName} ${person.lastName}`}
                          </Typography>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2" sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'text.secondary'
                            }}>
                              <EmailIcon sx={{ fontSize: 18 }} />
                              {person.email}
                            </Typography>

                            <Typography variant="body2" sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'text.secondary'
                            }}>
                              <PhoneIcon sx={{ fontSize: 18 }} />
                              {person.phone}
                            </Typography>

                            <Typography variant="body2" sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'text.secondary'
                            }}>
                              <BusinessIcon sx={{ fontSize: 18 }} />
                              {person.service}
                            </Typography>

                            <Typography variant="body2" sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              color: 'text.secondary'
                            }}>
                              <AccessTimeIcon sx={{ fontSize: 18 }} />
                              Total des heures: {person.schedule ? `${calculateWeeklyHours(person.schedule)}h00` : '0h00'}
                            </Typography>
                          </Box>
                        </CardContent>

                        <CardActions sx={{ 
                          p: 2, 
                          pt: 0,
                          display: 'flex',
                          justifyContent: 'center',
                          borderTop: '1px solid rgba(0, 0, 0, 0.12)'
                        }}>
                          <Tooltip title="Détails">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setSelectedPersonnel(person);
                                setDetailsOpen(true);
                              }}
                              sx={{ color: 'info.main' }}
                            >
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={selectedTab} index={2}>
          <AbsenceManagement />
        </TabPanel>

        <TabPanel value={selectedTab} index={3}>
          <Box p={3}>
            <Button
              variant="contained"
              startIcon={<MessageIcon />}
              onClick={() => navigate('/admin/messages')}
            >
              Accéder aux Messages
            </Button>
            {/* Contenu de l'onglet Messages */}
          </Box>
        </TabPanel>

        <TabPanel value={selectedTab} index={4}>
          <Settings />
        </TabPanel>
      </Paper>

      {/* Modal des détails du personnel */}
      {selectedPersonnel && (
        <PersonnelDetails
          open={detailsOpen}
          onClose={() => {
            setDetailsOpen(false);
            setSelectedPersonnel(null);
          }}
          personnel={selectedPersonnel}
        />
      )}
    </Container>
  );
};

export default AdminDashboard;
